import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import {
  AnimatePresence,
  motion,
  type TargetAndTransition,
  type Transition,
} from "motion/react";
import { useShallow } from "zustand/react/shallow";
import { useAppStore } from "@/stores";
import { selectPrizeDropById } from "@/stores/prizedrop.store";
import { PrizeText } from "@/components/prize-text";
import { SpeechBubble } from "@/igw-button/speech-bubble";
import {
  FADE_ON_FRAMELOOP,
  FADE_SPRING,
  speechPose,
  type SpeechPose,
} from "@/igw-button/fade";
import { Badge } from "@/components/ui/badge";
import { Gleam } from "@/igw-button/gleam";
import { HourglassIcon } from "@/igw-button/icons";
import {
  METAL_FROM,
  METAL_SPRING,
  metalArrive,
  metalLeave,
} from "@/igw-button/metal";
import { usePrizeDropLayout } from "@/igw-button/dev/prizedrop-layout";
import { formatButtonPrizeTitle } from "@/utils/helpers";
import type { LauncherContentProps } from "../../registry";
import type { PrizeDrop, PrizeDropPrize } from "./types";

/** How long a tier rests on the strip before the next one comes out
 *  (Sebastian, 2026-10-22: "increase the rotation time on the prize drop
 *  prize chips", from 3.2s — then "change from 12 to 8s"); a big drop's tier
 *  holds as long (it was 4.2s, a beat over the old rest). A hot period never
 *  races it. */
const DWELL_MS = 8000;
const DWELL_AFTER_DROP_MS = 8000;
/** The reel rests after this many rounds (MOTION-04) and wakes on a big
 *  beat, a hot period or a new board. */
const ROUNDS_BEFORE_REST = 2;
/** The prize's type on the strip (12px, 11px on a phone) and its floor. */
const PRIZE_PX = 12;
const FLOOR_PX = 10;
/** A tier's prize fits its strip at the chips' max: down to a step past the
 *  button's floor before it glides (Sebastian, 2026-10-22: "we can also
 *  scale the prize text more before scrolling"; `useFitScale`). Its count
 *  rides on its own chip, a size under it (`TierCount`). */
const STRIP_FLOOR_PX = 9;
/** The title chip's words shrink to the floor once the chip is at the
 *  shared max width ("the label chips should have a shared max length before
 *  we start to scale the text"), then end in an ellipsis. */
const TITLE_FLOOR_PX = 10;
/** A tier's count turns urgent at the last few (COPY-05). */
const LAST_FEW = 3;
/** A drop from a tier this scarce (or the last of any tier) is a big one:
 *  the slam, the flash and the bubble — by the data, not a "top" tier. */
const SCARCE = 10;
/** How long a bubble ("Hot right now!", "€100 just dropped!") stands. */
const BUBBLE_MS = 3000;
/** Where the prize sits in a translated "{{prize}} just dropped!". */
const MARK = "\u2063";
/** At most one big drop beat in this long (IGB-PD-05). */
const BEAT_GAP_MS = 10_000;

const STILL: Transition = { duration: 0 };
/** A tier swapping for the next (`TierChip`; Sebastian, 2026-10-22: "the
 *  swap transition makes no sense — I want a clearer understanding of how
 *  they are being swapped"): the coin hands them out. The old chip slides
 *  back under the coin, the button's own leave, and the next slides out from
 *  under it a beat later, the button's own entry — one at a time, one way,
 *  so it reads as the next prize coming out. (Crossfaded in place, the
 *  overlap thinned and flashed; dropped in place, it read as nothing.) A big
 *  drop's tier comes out the same way on a heavier mass (graded by the event,
 *  IGB-MOTION-01; the slab's flash and the bubble carry the rest). Only a
 *  short way, fading as it goes — not the chip's whole width back under the
 *  coin (Sebastian, 2026-10-22: "the movement is too big — don't move so much
 *  on the x-axis, calm it down a bit"). */
const TIER_TRAVEL_PX = 24;
const TIER_FROM = { x: -TIER_TRAVEL_PX, opacity: 0 } as const;
const TIER_IN_DELAY_S = 0.1;
const TIER_IN_BIG: Transition = {
  x: {
    type: "spring",
    stiffness: 1500,
    damping: 95,
    mass: 1.6,
    delay: TIER_IN_DELAY_S,
  },
  opacity: { duration: 0.07, delay: TIER_IN_DELAY_S },
};
const TIER_LEAVE = {
  ...TIER_FROM,
  transition: { x: METAL_SPRING, opacity: { duration: 0.12, ease: "easeIn" } },
} as const;
/** A count changing on its tier (`TierCount`: a drop ticked it): the pill
 *  swaps in place — the new one in from just above, on top and opaque
 *  within 80ms, on a stiff, all but critically damped spring; the old one
 *  holds still and whole under it, then fades. It moves, never scales
 *  (metal, not jelly — 2026-10-21: "no scaling or wobbly"). */
const CHIP_IN: Transition = { type: "spring", stiffness: 900, damping: 60 };
const CHIP_OPAQUE_S = 0.08;
const CHIP_SWAP = {
  in: { y: -5, opacity: 0 },
  show: { y: 0, opacity: 1 },
  out: {
    opacity: 0,
    transition: { delay: CHIP_OPAQUE_S, duration: 0.1, ease: "linear" },
  },
} as const;
const COUNT_IN: Transition = {
  default: CHIP_IN,
  opacity: { duration: CHIP_OPAQUE_S, ease: "linear" },
};
/** A routine drop's glint along the slant, fading. */
const GLINT: Transition = { type: "spring", stiffness: 90, damping: 20 };
/** Entry: the button's metal slide out from under the coin
 *  (`igw-button/metal`), a tad late so the weight shows (Sebastian,
 *  2026-10-21: "delay the entry just a tad"). */
const ENTER = METAL_FROM;
const ENTER_AT = 0.1;
const arrive = metalArrive;
/** Leaving: the entry reversed (Sebastian, 2026-10-21: "ignore the
 *  snapping, let's do a reversed entry instead") — back under the coin along
 *  x, with the same weight, at full ink (the set's clip hides it); the strip
 *  first (it came last), then the counter. */
const STRIP_GONE = metalLeave();
const COUNTER_GONE = metalLeave(0.07);
/** Hot's entry and exit: the charge, its glow and the strip's lit foot
 *  fading up together (and back down — Sebastian, 2026-10-21: "fade in the
 *  bg gradient instead of slide in"), on the launcher's fade spring and its
 *  frame loop (no end-of-fade flash, D-100). */
const CHARGE_OFF = { opacity: 0 };
const CHARGE_ON = { opacity: 1 };
/** A big drop while the title chip is hot: its edge flares white-hot for a
 *  beat, then settles back into the heat. */
const FLARE: TargetAndTransition = {
  opacity: [0, 1, 0],
  transition: {
    duration: 1.1,
    times: [0, 0.15, 1],
    ease: "easeOut",
    delay: 0.1,
  },
};
/** The big beat: the slab's gold flash fading, the slab's thud (a short
 *  drop that sets back, with weight — no scaling). */
const FLASH: Transition = { type: "spring", stiffness: 60, damping: 20 };
const THUD: Transition = {
  type: "spring",
  stiffness: 1200,
  damping: 85,
  mass: 1.6,
};

/**
 * A prize drop on the in-game button — its own build, taken as far as the
 * type needs (as the tournament became a wheel and missions a checklist
 * cell; Sebastian, 2026-10-20: "see at what lengths we take different
 * campaign types based on the needs"): the label layout (Sebastian,
 * 2026-10-21: "we will go with the label layout"; the total-led slab it
 * replaced is gone, 2026-10-22). Two rows:
 * - the title chip: a slab out of the coin, its end slanted (falling), the
 *   type's name ("Prize Drops", its words editable in the dev frame) — it
 *   carries the states;
 * - the rolling drops under it: a gold strip from behind the coin, its end
 *   continuing the slab's slant, the tiers falling through it one at a time
 *   — "€20 · 7 left", a finite batch (a figure, so it holds at hundreds of
 *   drops and at one; each tier says its own last few).
 * Its states:
 * - idle: the card surface, lit out of the coin (fills run left to right);
 * - a drop: a glint runs along the slant (other players' routine drops tick
 *   quietly, D-080);
 * - a big drop (a scarce tier's, or a tier's last; ≤ 1 per 10s): the slab
 *   flashes gold (hot: its edge flares white-hot), a speech bubble names it
 *   ("€100 just dropped!"), and that tier comes out of the coin with weight;
 * - hot (the hot-period hook): a bubble as it starts ("Hot right now!"),
 *   then the chip molten metal, hottest at its end (IGB-PD-11), the gleam
 *   sweeping it.
 * Not opted in, finished and all dropped are the shared set's (`rows`).
 */
export function PrizeDropContent({
  campaignKey,
  reduced,
}: LauncherContentProps) {
  const { t, i18n } = useTranslation();
  // Counts grouped the locale's way ("1,250"; Sebastian: "group").
  const count = new Intl.NumberFormat(i18n.language);
  const pd = useAppStore(
    useShallow((s) => {
      const found = selectPrizeDropById(s, campaignKey);
      return found ? board(found) : null;
    })
  );
  const hot = useHot(pd?.campaignId ?? null);
  const drop = useDrop(pd?.tiers ?? []);
  // A win of yours waiting to be revealed: no "just dropped" bubble — it
  // could be your own drop, and the reveal is yours to have (👺 Belmont, B2).
  const yoursPending = useAppStore((s) => campaignKey in s.unrevealedWins);
  const bubble = useBubble(yoursPending ? null : drop.big, hot);
  // Every tier alike, rarest first (Sebastian: no top-tier treatment).
  const stops = (pd?.tiers ?? []).filter((tier) => tier.left_to_win > 0);
  const step = useReel(stops, drop, hot);
  const layout = usePrizeDropLayout();
  const rootRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  useFitScale(
    titleRef,
    {
      varName: "--igw-drop-title-scale",
      floorPx: TITLE_FLOOR_PX,
      room: titleRoom,
    },
    // The counter remounts on a big drop: fit the new title.
    [layout.label, hot, drop.big?.n]
  );
  // Where the title chip's words end, for the heat to keep off them.
  useWordsEnd(rootRef, [drop.big?.n, layout.label]);
  if (!pd) return null;
  const tierAt = (s: number): PrizeDropPrize | undefined => {
    const forced = step.forced.get(s);
    const pinned = forced && pd.tiers.find((tier) => tier.id === forced);
    if (pinned) return pinned;
    const n = stops.length;
    return n ? stops[((s % n) + n) % n] : undefined;
  };
  const tier = tierAt(step.at);
  return (
    <span
      ref={rootRef}
      className="igw-drop"
      data-still={reduced || undefined}
      data-hot={hot || undefined}
    >
      <span className="igw-drop__stack">
        <motion.span
          key={drop.big?.n ?? 0}
          className="igw-drop__counter"
          data-part="counter"
          // Entry: out from under the coin along x only, stopping with
          // weight; a big beat's remount: the thud.
          initial={reduced ? false : drop.big ? { y: 3 } : ENTER}
          animate={{
            x: 0,
            y: 0,
            opacity: 1,
            transition: drop.big ? { ...THUD, delay: 0.1 } : arrive(ENTER_AT),
          }}
          exit={reduced ? undefined : COUNTER_GONE}
        >
          {/* Hot: the glow round the slab — a blurred copy of its shape in
              the charge, fading with it (an animated filter would repaint
              its blur every frame). */}
          <AnimatePresence initial={false}>
            {hot && (
              <motion.span
                key="glow"
                className="igw-drop__glow"
                aria-hidden="true"
                initial={reduced ? false : CHARGE_OFF}
                animate={CHARGE_ON}
                exit={reduced ? undefined : CHARGE_OFF}
                transition={reduced ? STILL : FADE_SPRING}
                onUpdate={FADE_ON_FRAMELOOP}
              />
            )}
          </AnimatePresence>
          <span className="igw-drop__rim" aria-hidden="true" />
          <span className="igw-drop__face" aria-hidden="true">
            {/* Hot: the charge — the missions bar's fill in the progress
                token — fades up over the slab, and back down when the period
                ends (Sebastian, 2026-10-21: "fade in the bg gradient"). */}
            {/* `initial={false}`: a big drop remounts the slab while hot —
                its heat stays, it doesn't fade in again. */}
            <AnimatePresence initial={false}>
              {hot && (
                <motion.span
                  key="charge"
                  className="igw-drop__charge"
                  initial={reduced ? false : CHARGE_OFF}
                  animate={CHARGE_ON}
                  exit={reduced ? undefined : CHARGE_OFF}
                  transition={reduced ? STILL : FADE_SPRING}
                  onUpdate={FADE_ON_FRAMELOOP}
                />
              )}
            </AnimatePresence>
            {/* Hot: the gleam sweeping the charged chip — no sparks, the
                heat itself moves (Sebastian, 2026-10-21: "calm down the
                other effects, remove the sparks"). */}
            {hot && !reduced && <Gleam face />}
          </span>
          {/* A routine drop: a glint along the slant, fading. */}
          {drop.n > 0 && !reduced && (
            <motion.span
              key={`glint-${drop.n}`}
              className="igw-drop__glint"
              aria-hidden="true"
              initial={{ opacity: 1 }}
              animate={{ opacity: 0, transition: GLINT }}
            />
          )}
          {/* A big drop: the slab's gold flash, fading — hot, its edge
              flaring white-hot instead (below). */}
          {drop.big && !reduced && !hot && (
            <motion.span
              key={`flash-${drop.big.n}`}
              className="igw-drop__flash"
              aria-hidden="true"
              initial={{ opacity: 1 }}
              animate={{ opacity: 0, transition: { ...FLASH, delay: 0.1 } }}
            />
          )}
          {drop.big && hot && !reduced && (
            <motion.span
              key={`flare-${drop.big.n}`}
              className="igw-drop__flare"
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={FLARE}
            />
          )}
          <span className="igw-drop__figure" data-part="title">
            {/* The title chip's words (Sebastian, 2026-10-21: "write in
                there 'Prize Drops' — this label can be changed in dev
                tools"): the type's own name, the slab's states around it. */}
            <Badge
              ref={titleRef}
              variant="text"
              color="foreground"
              size="inherit"
              className="igw-drop__title"
            >
              {layout.label || t("prizedrop.tabLabel", "Prize Drops")}
            </Badge>
          </span>
        </motion.span>
        {/* The drops, directly under the counter (Sebastian, 2026-10-20:
            "put them directly under"): one tier at a time, each its own
            prize chip at its own prize's width — the chip swaps, never
            just its words (2026-10-22: "when we replace the chip instead of
            swapping the labels they can have the correct width for their
            labels"). */}
        {tier && (
          <motion.span
            className="igw-drop__strip-slot"
            data-part="drops"
            // It slides out from under the counter, with weight.
            initial={reduced ? false : ENTER}
            animate={{
              x: 0,
              opacity: 1,
              transition: arrive(ENTER_AT + 0.07),
            }}
            exit={reduced ? undefined : STRIP_GONE}
          >
            <AnimatePresence initial={false}>
              <TierChip
                // The step and its tier: when the tiers still dropping
                // change (one sells out), the same step can point at another
                // tier — that is a new chip, never its words rewritten.
                key={`${step.at}:${tier.id}`}
                tier={tier}
                big={step.forced.has(step.at)}
                hot={hot}
                format={count}
                reduced={reduced}
              />
            </AnimatePresence>
          </motion.span>
        )}
        {/* The moments in words, a speech bubble over the counter: a hot
            period starting, another player's big drop (Sebastian,
            2026-10-20: "we can use speech bubble for other players' big
            drops"). */}
        <AnimatePresence>
          {/* Words, so they show under reduced motion too, held still
              (MOTION-02; 👺 Belmont, B1). */}
          {bubble && (
            <SpeechBubble
              key={bubble.key}
              pose={bubble.pose}
              still={reduced}
              // A hot period's bubble wears the heat (`SpeechTone`).
              tone={bubble.kind === "hot" ? "hot" : undefined}
              className="igw-drop__bubble"
            >
              {bubble.kind === "hot" ? (
                t("prizedrop.hotNow", "Hot right now!")
              ) : (
                <JustDropped tier={bubble.tier} />
              )}
            </SpeechBubble>
          )}
        </AnimatePresence>
      </span>
    </span>
  );
}

/** A tier on the strip: its prize (the crown's dark ink on the gold; it
 *  shrinks, then glides — never cut). How many are left rides on the
 *  strip's end (`TierCount`). */
function TierLine({ tier }: { tier: PrizeDropPrize }) {
  const { t } = useTranslation();
  return (
    <>
      <span className="igw-drop__prize igw-prize-chip__prize">
        <PrizeText
          size="inherit"
          tone="onGold"
          // The line is fitted (`TierChip`); the prize itself only glides.
          minScale={1}
          // No emboss: on the bar's gradient its 1px light offset read as a
          // jagged edge (Sebastian, 2026-10-21) — the dark ink alone is crisp.
          shadow={false}
          currencyMark={!tier.is_catalog_item}
          fit="marquee"
          className="min-w-0"
        >
          {formatButtonPrizeTitle(tier.label, t)}
        </PrizeText>
      </span>
    </>
  );
}

/** How many of the tier on show are left: "×20" on a chip of its own,
 *  centred on its strip's end — the shared prize chip as a pill, in the same
 *  material — so the prize on the strip leads (Sebastian, 2026-10-22: "now
 *  the 20 left is more readable than the prize itself"; "put the ×N badge as
 *  a chip on the right side of the prize chip, style it in a similar
 *  manner"). A new tier or a new count swaps the whole chip, not its figure
 *  — a chip, never a label being rewritten ("a quick snappy chip swap";
 *  `CHIP_SWAP`); a new tier brings its own, on its chip. The last few: the urgent token and the hourglass. */
function TierCount({
  tier,
  format,
  reduced,
}: {
  tier: PrizeDropPrize;
  format: Intl.NumberFormat;
  reduced: boolean;
}) {
  const { t } = useTranslation();
  const left = tier.left_to_win;
  const urgent = left > 0 && left <= LAST_FEW && tier.quantity > LAST_FEW;
  const dockRef = useRef<HTMLSpanElement>(null);
  // Its strip keeps the pill's inner half clear: its width, as it changes
  // (on the tier's chip, which the strip inherits it from).
  useLayoutEffect(() => {
    const dock = dockRef.current;
    const slot = dock?.parentElement;
    if (!dock || !slot) return;
    const apply = () =>
      slot.style.setProperty("--igw-drop-count-w", `${dock.offsetWidth}px`);
    apply();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(apply);
    observer.observe(dock);
    return () => observer.disconnect();
  }, []);
  return (
    <span ref={dockRef} className="igw-drop__count" data-part="tier-left">
      <AnimatePresence initial={false}>
        {/* None left: no count — a "×0" says nothing (the drained chip
            does). */}
        {left > 0 && (
          <motion.span
            key={`${tier.id}:${left}`}
            className="igw-prize-chip-holder igw-drop__count-chip"
            data-urgent={urgent || undefined}
            initial={reduced ? false : CHIP_SWAP.in}
            animate={CHIP_SWAP.show}
            exit={reduced ? undefined : CHIP_SWAP.out}
            transition={reduced ? STILL : COUNT_IN}
          >
            <span className="igw-prize-chip igw-prize-chip--pill igw-prize-chip--pill-sm">
              <span className="igw-prize-chip__prize igw-prize-chip__figure">
                {/* Urgency is never colour alone (D-087). */}
                {urgent && (
                  <HourglassIcon className="igw-drop__tier-hourglass" />
                )}
                {t("prizeReveal.countChip", "×{{count}}", {
                  count: left,
                  replace: { count: format.format(left) },
                })}
              </span>
            </span>
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

/** "{{prize}} just dropped!" with the prize as itself (shrinks, then
 *  glides — never cut), in any language's word order. */
function JustDropped({ tier }: { tier: PrizeDropPrize }) {
  const { t } = useTranslation();
  const line = t("prizedrop.justDropped", { prize: MARK });
  const at = line.indexOf(MARK);
  const before = at < 0 ? "" : line.slice(0, at);
  const after = at < 0 ? line : line.slice(at + MARK.length);
  return (
    <span className="igw-drop__said">
      {before}
      <PrizeText
        size="inherit"
        tone="onGold"
        minScale={FLOOR_PX / PRIZE_PX}
        currencyMark={!tier.is_catalog_item}
        fit="marquee"
        className="min-w-0"
      >
        {formatButtonPrizeTitle(tier.label, t)}
      </PrizeText>
      {after}
    </span>
  );
}

/** What the content reads off a prize drop (a stable shape for the store's
 *  shallow compare). */
function board(pd: PrizeDrop) {
  return {
    campaignId: pd.campaign.id,
    tiers: pd.prizes,
  };
}

interface Drop {
  /** Counts the drops seen (0: none yet). */
  n: number;
  /** The last big one — a scarce tier's drop or a tier's last — at most one per
   *  `BEAT_GAP_MS`: the tier slams onto the strip, the slab flashes, the
   *  bubble names it. */
  big: { n: number; tierId: string; tier: PrizeDropPrize } | null;
}

/** A prize dropped: a tier's count went down since the last push (the first
 *  sight of the board is no drop). Graded (D-080 / IGB-PD-05). */
function useDrop(tiers: PrizeDropPrize[]): Drop {
  const seen = useRef<Map<string, number> | null>(null);
  const lastBig = useRef(0);
  const [drop, setDrop] = useState<Drop>({ n: 0, big: null });
  useEffect(() => {
    const before = seen.current;
    seen.current = new Map(tiers.map((tier) => [tier.id, tier.left_to_win]));
    if (!before) return;
    const dropped = tiers.filter(
      (tier) => tier.left_to_win < (before.get(tier.id) ?? tier.left_to_win)
    );
    if (!dropped.length) return;
    const big = dropped.find(
      (tier) => tier.left_to_win === 0 || tier.quantity <= SCARCE
    );
    const now = Date.now();
    const beat = big && now - lastBig.current >= BEAT_GAP_MS ? big : null;
    if (beat) lastBig.current = now;
    setDrop((d) => ({
      n: d.n + 1,
      big: beat ? { n: d.n + 1, tierId: beat.id, tier: beat } : d.big,
    }));
  }, [tiers]);
  return drop;
}

/** The reel's step: on after a dwell, at once onto a big drop's tier (held
 *  there: `forced`), resting after two rounds and woken by a big beat, a
 *  hot period starting or a new board. It never wraps, so each tier on show
 *  keeps a key of its own. */
function useReel(stops: PrizeDropPrize[], drop: Drop, hot: boolean) {
  const [at, setAt] = useState(0);
  const forced = useRef(new Map<number, string>());
  const lastBig = useRef(0);
  const wokeAt = useRef(0);
  const n = stops.length;
  const board = stops.map((stop) => stop.id).join(",");
  const bigN = drop.big?.n ?? 0;
  useEffect(() => {
    wokeAt.current = at;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bigN, board]);
  useEffect(() => {
    if (hot) wokeAt.current = at;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hot]);
  useEffect(() => {
    const big = drop.big;
    if (!big || big.n === lastBig.current) return;
    lastBig.current = big.n;
    setAt((s) => {
      forced.current.set(s + 1, big.tierId);
      return s + 1;
    });
  }, [drop.big]);
  useEffect(() => {
    const held = forced.current.has(at);
    if (n < 2 && !held) return;
    if (!held && at - wokeAt.current >= ROUNDS_BEFORE_REST * n) return;
    const id = window.setTimeout(
      () => setAt((s) => s + 1),
      held ? DWELL_AFTER_DROP_MS : DWELL_MS
    );
    return () => window.clearTimeout(id);
  }, [at, n, bigN, hot, board]);
  return { at, forced: forced.current };
}

type Bubble = { key: string; pose: SpeechPose } & (
  | { kind: "hot" }
  | { kind: "drop"; tier: PrizeDropPrize }
);

/** The bubble over the counter: a big drop's prize (another player's — a
 *  win of yours is the reveal's), or a hot period starting; one at a time,
 *  each for `BUBBLE_MS`. */
function useBubble(big: Drop["big"], hot: boolean): Bubble | null {
  const [bubble, setBubble] = useState<Bubble | null>(null);
  // One timer for whichever bubble is up, cancelled only by the next one or
  // the unmount: tied to an effect, a period ending (or the big drop moving
  // on) inside the bubble's 3s cancelled it, and the bubble never left.
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const show = (next: Bubble) => {
    setBubble(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setBubble(null), BUBBLE_MS);
  };
  useEffect(() => {
    if (!big) return;
    show({
      kind: "drop",
      key: `drop-${big.n}`,
      pose: speechPose(),
      tier: big.tier,
    });
  }, [big]);
  useEffect(() => {
    if (hot) show({ kind: "hot", key: "hot", pose: speechPose() });
    // The period over: its "Hot right now!" goes with it.
    else setBubble((b) => (b?.kind === "hot" ? null : b));
  }, [hot]);
  return bubble;
}

/** Where the title chip's words end, px from the slab's start, written as
 *  `--igw-drop-words` on the content's root (layout px, so a zoomed host
 *  doesn't skew it): the heat's bright stops burn only past it, so the words
 *  keep their contrast (👺 Belmont: white words on the bright stops read
 *  ~1.9:1; Sebastian, 2026-10-21: "keep the heat off the words"). Re-read as
 *  the words or the slab change size (a hot swell, a label edit, a big
 *  drop's remount). */
function useWordsEnd(
  rootRef: RefObject<HTMLSpanElement | null>,
  deps: unknown[]
) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const counter = root.querySelector<HTMLElement>(".igw-drop__counter");
    const title = root.querySelector<HTMLElement>(".igw-drop__title");
    if (!counter || !title || typeof ResizeObserver !== "function") return;
    const apply = () => {
      let end = title.offsetWidth;
      for (
        let el: HTMLElement | null = title;
        el && el !== counter;
        el = el.offsetParent as HTMLElement | null
      )
        end += el.offsetLeft;
      root.style.setProperty("--igw-drop-words", `${end + 4}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(title);
    observer.observe(counter);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** The tier on show as its own chip: the strip (the prize chip, as wide as
 *  its prize within the chips' bounds; its line fitted as one) with its
 *  count on its end — swapping in and out as one. */
function TierChip({
  tier,
  big,
  hot,
  format,
  reduced,
}: {
  tier: PrizeDropPrize;
  /** A big drop's tier (a forced step): out on the heavier mass. */
  big: boolean;
  hot: boolean;
  format: Intl.NumberFormat;
  reduced: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useFitScale(
    ref,
    {
      varName: "--igw-drop-line-scale",
      floorPx: STRIP_FLOOR_PX,
      lead: ".igw-drop__prize",
      room: lineRoom,
      // On the tier: its count reads the prize's fitted size (`.igw-drop__count`).
      on: (node) => node.closest<HTMLElement>(".igw-drop__tier"),
    },
    [tier.label]
  );
  return (
    <motion.span
      className="igw-drop__tier"
      // Sold out: the chip and its count drain together until the reel
      // moves on (👺 Belmont, B3).
      data-out={tier.left_to_win === 0 || undefined}
      initial={reduced ? false : TIER_FROM}
      animate={{
        x: 0,
        opacity: 1,
        transition: reduced
          ? STILL
          : big
            ? TIER_IN_BIG
            : arrive(TIER_IN_DELAY_S),
      }}
      exit={reduced ? undefined : TIER_LEAVE}
    >
      {/* The chip's edge round its clipped shape (`.igw-prize-chip-holder`). */}
      <span className="igw-prize-chip-holder igw-drop__tier-holder">
        <span className="igw-drop__strip igw-prize-chip">
          {/* Hot: the strip's foot lit in the charge, fading with it. */}
          <AnimatePresence initial={false}>
            {hot && (
              <motion.span
                key="foot"
                className="igw-drop__foot"
                aria-hidden="true"
                initial={reduced ? false : CHARGE_OFF}
                animate={CHARGE_ON}
                exit={reduced ? undefined : CHARGE_OFF}
                transition={reduced ? STILL : FADE_SPRING}
                onUpdate={FADE_ON_FRAMELOOP}
              />
            )}
          </AnimatePresence>
          <span ref={ref} className="igw-drop__line igw-drop__stop">
            <TierLine tier={tier} />
          </span>
        </span>
      </span>
      <TierCount tier={tier} format={format} reduced={reduced} />
    </motion.span>
  );
}

/** The room for a tier's line: its chip at the max width, less the strip's
 *  pads (the count's clearance among them). */
function lineRoom(node: HTMLElement): number {
  const chip = node.closest<HTMLElement>(".igw-drop__tier");
  const strip = node.closest<HTMLElement>(".igw-drop__strip");
  if (!chip || !strip) return node.clientWidth;
  const cs = getComputedStyle(strip);
  return (
    parseFloat(getComputedStyle(chip).maxWidth) -
    parseFloat(cs.paddingLeft) -
    parseFloat(cs.paddingRight)
  );
}

/** The room for the title's words: the chip at its max width, less its
 *  pads. */
function titleRoom(node: HTMLElement): number {
  const chip = node.closest<HTMLElement>(".igw-drop__counter");
  if (!chip) return node.offsetWidth;
  const cs = getComputedStyle(chip);
  return (
    parseFloat(cs.maxWidth) -
    parseFloat(cs.paddingLeft) -
    parseFloat(cs.paddingRight)
  );
}

/**
 * Fits a line of type into its room as one: its full-size width read off a
 * hidden copy released to its content, then `varName` (the scale its CSS
 * sizes the type by) set on the node so it fits — never shrinking its lead
 * text (`lead`, or the node itself) below `floorPx`; what is still over
 * glides (the prize's marquee) or ends in an ellipsis (the title). The copy
 * measures, so the live node keeps its own transitions (the title's hot
 * swell eases; 👺 Belmont, B2). `room` is the room at full size. Written on
 * the node, no re-render; read again when the node's box resizes.
 */
function useFitScale(
  ref: RefObject<HTMLElement | null>,
  {
    varName,
    floorPx,
    lead,
    room,
    on,
  }: {
    varName: string;
    floorPx: number;
    lead?: string;
    room: (node: HTMLElement) => number;
    /** Where the scale is written (the node itself unless given): an
     *  ancestor, so a sibling of the node sizes itself by it too. */
    on?: (node: HTMLElement) => HTMLElement | null;
  },
  deps: unknown[]
) {
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const fit = () => {
      const parent = node.parentElement;
      if (!parent) return;
      const copy = node.cloneNode(true) as HTMLElement;
      copy.setAttribute("aria-hidden", "true");
      Object.assign(copy.style, {
        position: "absolute",
        left: "0",
        top: "0",
        visibility: "hidden",
        pointerEvents: "none",
        transition: "none",
        animation: "none",
        width: "max-content",
        maxWidth: "none",
      });
      copy.style.setProperty(varName, "1");
      parent.appendChild(copy);
      const natural = copy.offsetWidth;
      const leadEl = (lead && copy.querySelector<HTMLElement>(lead)) || copy;
      const base = parseFloat(getComputedStyle(leadEl).fontSize);
      copy.remove();
      const avail = room(node);
      const scale =
        natural > avail && base > 0
          ? Math.max(Math.min(1, floorPx / base), avail / natural)
          : 1;
      (on?.(node) ?? node).style.setProperty(varName, String(scale));
    };
    fit();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(() => fit());
    if (node.parentElement) observer.observe(node.parentElement);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Whether the drop runs hot now, and back to cool when its time is up. */
function useHot(campaignId: number | null): boolean {
  const until = useAppStore((s) =>
    campaignId === null ? 0 : (s.prizeDropHotUntil[campaignId] ?? 0)
  );
  // The hot period that has run out (its `until`): cool again.
  const [over, setOver] = useState(0);
  useEffect(() => {
    const left = until - Date.now();
    if (left <= 0) return;
    const id = window.setTimeout(() => setOver(until), left);
    return () => window.clearTimeout(id);
  }, [until]);
  return until > 0 && over !== until;
}
